"use client";

import { EditorContent } from "@tiptap/react";
import * as React from "react";

// --- Hooks ---
import { useUiEditorState } from "../../../hooks/use-ui-editor-state";

// --- Tiptap UI ---
import { DragContextMenu } from "../../tiptap-ui/drag-context-menu";
import { EmojiDropdownMenu } from "../../tiptap-ui/emoji-dropdown-menu";
import { SlashDropdownMenu } from "../../tiptap-ui/slash-dropdown-menu";
// --- Contexts ---
import { AiProvider } from "../../../contexts/ai-context";
import { AppProvider } from "../../../contexts/app-context";
import { generateRandomColor } from "../../../contexts/user-context";

// --- Lib ---
// import {
//   handleImageUpload,
//   MAX_FILE_SIZE,
// } from "../../../lib/tiptap-utils";
// import { TIPTAP_AI_APP_ID } from "../../../lib/tiptap-collab-utils";

// --- Styles ---
import "./notion-like-editor.scss";

// --- Content ---
import { cn } from "../../../lib/tiptap-utils";
import { EditorProvider, useEditorContext } from "../../EditorProvider";
import { EditorProps } from "../../types";
import { MobileToolbar } from "./notion-like-editor-mobile-toolbar";
import { NotionToolbarFloating } from "./notion-like-editor-toolbar-floating";

/**
 * EditorContent component that renders the actual editor
 */
const EditorContentArea: React.FC = () => {
  const { editor } = useEditorContext();
  const {
    aiGenerationIsLoading,
    aiGenerationIsSelection,
    aiGenerationHasMessage,
  } = useUiEditorState(editor);

  // Selection based effect to handle AI generation acceptance
  React.useEffect(() => {
    if (!editor) return;

    if (
      !aiGenerationIsLoading &&
      aiGenerationIsSelection &&
      aiGenerationHasMessage
    ) {
      // editor.chain().focus().aiAccept().run();
      editor.commands.resetUiState();
    }
  }, [
    aiGenerationHasMessage,
    aiGenerationIsLoading,
    aiGenerationIsSelection,
    editor,
  ]);

  if (!editor) {
    return null;
  }

  return (
    <EditorContent
      editor={editor}
      role="presentation"
      className="notion-like-editor-content"
      style={{
        cursor: editor.view.dragging ? "grabbing" : "auto",
      }}
    >
      <DragContextMenu />
      <MobileToolbar />
      {/* <AiMenu /> */}
      <EmojiDropdownMenu />
      {/* <MentionDropdownMenu /> */}
      <SlashDropdownMenu />
      <NotionToolbarFloating />
    </EditorContent>
  );
};

export const BlockEditorProvider: React.FC<
  Pick<
    EditorProps,
    "onUpdate" | "placeholder" | "user" | "connectToRemoteSync"
  > & {
    children: React.ReactNode;
  }
> = ({ children, placeholder = "Start writing...", ...props }) => {
  return (
    <AppProvider>
      <AiProvider>
        <EditorProvider
          {...props}
          placeholder={placeholder}
          placeholderEmptyNodeClass="is-empty with-slash"
          user={{ ...props.user, color: generateRandomColor() }}
          editorProps={{
            attributes: {
              class: "notion-like-editor",
            },
          }}
          onUpdate={props.onUpdate}
        >
          {children}
        </EditorProvider>
      </AiProvider>
    </AppProvider>
  );
};

/**
 * Internal component that handles the editor loading state
 */
export const BlockEditorContent: React.FC<{ className?: string }> = ({
  className,
}) => {
  return (
    <div className={cn("notion-like-editor-wrapper", className)}>
      <EditorContentArea />
    </div>
  );
};
